import {Injectable} from '@angular/core';
import {CardConfig, CardConfigPayload, ConsignmentCardConfig} from '@app/model/dcp-config-service/card-config';
import {AddressUsage} from '@app/model/order/dcp-order-entry.model';
import {PaymentTypeCode} from '@app/model/order/dcp-order.model';
import {ConsignmentMetaData} from '@app/model/payment/consignment-meta-data.model';
import {EntryGroupService} from '@app/product/services/entry-group.service';
import {CreditCardService} from '@app/services/credit-card.service';
import {DcpApiService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActiveCartFacade, Cart, OrderEntry} from '@spartacus/cart/base/root';
import {Address, PaymentDetails, User, UserIdService, UserPaymentService} from '@spartacus/core';
import {BehaviorSubject, Observable, of, ReplaySubject} from 'rxjs';
import {first, map, switchMap, take, tap} from 'rxjs/operators';
import {PaymentProvider} from './dcp-cc-psp.service';

import {CheckoutPaymentTypeFacade} from '@spartacus/checkout/b2b/root';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpCheckoutPaymentService {
  /**
   * This ReplaySubject is being used to temporarily store the selected payment type, which
   * then will be set when proceeding to review-order via cta button
   */
  private currentPaymentType: ReplaySubject<PaymentTypeCode> = new ReplaySubject<PaymentTypeCode>(1);
  /**
   * This BehaviorSubject is being used to temporarily store the selected PaymentDetails
   * since setPaymentDetails() triggers OCC api
   */
  private currentPaymentDetails: BehaviorSubject<PaymentDetails> = new BehaviorSubject<PaymentDetails>(null);
  cacheCardConfig = {};

  constructor(
    protected activeCartFacade: ActiveCartFacade,
    protected userIdService: UserIdService,
    protected entryGroupService: EntryGroupService,
    protected creditCardService: CreditCardService,
    private dcpApiService: DcpApiService,
    private userPaymentService: UserPaymentService,
    private checkoutPaymentFacade: CheckoutPaymentTypeFacade
  ) {}

  public getCurrentPaymentDetails(): Observable<PaymentDetails> {
    return this.currentPaymentDetails.asObservable();
  }

  public setCurrentPaymentDetails(newPaymentDetails: PaymentDetails) {
    this.currentPaymentDetails.next(newPaymentDetails);
  }

  public getCurrentPaymentType(): Observable<PaymentTypeCode> {
    return this.currentPaymentType.asObservable();
  }

  public setCurrentPaymentType(typeCode: PaymentTypeCode): void {
    this.currentPaymentType.next(typeCode);
    this.checkoutPaymentFacade.setPaymentType(typeCode);
  }

  public getCardConfig(
    vendor: string,
    salesOrg: string,
    paymentProvider = PaymentProvider.DELEGO
  ): Observable<CardConfig> {
    const cacheKey = `${vendor}_${salesOrg}_${paymentProvider}`;
    if (this.cacheCardConfig[cacheKey]) {
      return of(this.cacheCardConfig[cacheKey]);
    }

    return this.activeCartFacade.getActive().pipe(
      take(1),
      switchMap((cart: Cart) => {
        const cardType = cart.paymentInfo?.cardType.code;
        const currencyIso = cart.totalPrice.currencyIso;
        const creditCardPayload: CardConfigPayload = {
          vendorId: vendor,
          salesOrgCode: salesOrg,
          httpParams: {
            paymentMode: 'creditcard',
            cardType,
            currency: currencyIso,
            lineOfBusiness: 'sap-application',
            countryCode: this.getAddressByUsage(cart, AddressUsage.SOLD_TO).country.isocode,
            paymentProvider,
          },
        };
        return this.creditCardService.getCardConfig(creditCardPayload);
      }),
      tap((cardConfig) => (this.cacheCardConfig[cacheKey] = cardConfig))
    );
  }

  /**
   *  Method saves full payment details, including custom fields like dpao subscription id,
   *  used instead an OOTB createPaymentDetails(preparedPaymentData), which is limited to save
   *  only some specific/hardcoded fields
   *
   * @param preparedPaymentData - payment details to be saved
   * @param userId - customer id
   */
  createFullCreditCardDetails(preparedPaymentData: PaymentDetails, userId = 'current'): Observable<any> {
    return this.activeCartFacade.getActive().pipe(
      first(),
      switchMap((cart) =>
        this.dcpApiService.callOcc<PaymentDetails>(
          'fullPaymentDetails',
          {
            userId,
            cartId: cart.code,
          },
          {body: preparedPaymentData},
          'POST'
        )
      ),
      tap((paymentDetails) => {
        this.userPaymentService.loadPaymentMethods();
        this.setCurrentPaymentDetails(paymentDetails);
      }),
      untilDestroyed(this)
    );
  }

  prepareBillingAddress(user: User): PaymentDetails {
    return {
      accountHolderName: user.name,
      billingAddress: {
        // using CP address for name data since unit address does not contain firstname & lastname
        firstName: user.firstName,
        lastName: user.lastName,
        line1: user.defaultUnitAddress.line1,
        line2: user.defaultUnitAddress.line2,
        postalCode: user.defaultUnitAddress.postalCode,
        town: user.defaultUnitAddress.town,
        addressUsage: user.defaultUnitAddress.addressUsage,
        cellphone: user.defaultUnitAddress.cellphone,
        companyName: user.defaultUnitAddress.companyName,
        country: {
          isocode: user.defaultUnitAddress.country.isocode,
          name: user.defaultUnitAddress.country.name,
        },
        defaultAddress: user.defaultUnitAddress.defaultAddress,
        district: user.defaultUnitAddress.district,
        email: user.defaultUnitAddress.email,
        formattedAddress: user.defaultUnitAddress.formattedAddress,
        id: user.defaultUnitAddress.id,
        phone: user.defaultUnitAddress.phone,
        shippingAddress: user.defaultUnitAddress.shippingAddress,
        title: user.defaultUnitAddress.title,
        visibleInAddressBook: user.defaultUnitAddress.visibleInAddressBook,
      },
    };
  }

  extractMetaData(consignmentCode: string): ConsignmentMetaData {
    if (typeof consignmentCode !== 'string') {
      throw new TypeError(`Expected a string for 'consignmentCode', but got ${typeof consignmentCode}`);
    }
    const [vendorAndSalesOrg, transactionType, endDate] = consignmentCode?.split('.');
    const [vendor, salesOrg] = vendorAndSalesOrg?.split('_') || [];
    return {code: consignmentCode, vendor, salesOrg, transactionType, endDate};
  }

  getConsignmentCodes(cart: Cart): string[] {
    const priceTotalsBySalesArea = JSON.parse(cart.data?.find((e) => e.type === 'priceTotalsBySalesArea')?.value);
    return Object.keys(priceTotalsBySalesArea);
  }

  extractConsignmentsMetaData(cart: Cart): ConsignmentMetaData[] {
    return this.getConsignmentCodes(cart).map(this.extractMetaData);
  }

  getVendorSalesOrg(cart: Cart): {vendor: string; salesOrg: string} {
    const vendor = this.extractConsignmentsMetaData(cart)[0].vendor;
    const salesOrg = JSON.parse(cart.entries[0].data.find((e) => e.type === 'salesArea').value).salesOrganizationERP;
    return {vendor, salesOrg};
  }

  /**
   * Maps consignments and corresponding cardConfig
   */
  getConsignmentCardConfigs(consignments: ConsignmentMetaData[]): Observable<ConsignmentCardConfig>[] {
    return consignments.map((consignment) =>
      this.getCardConfig(consignment.vendor, consignment.salesOrg).pipe(
        first(),
        map((cardConfig) => ({consignment, cardConfig})),
        untilDestroyed(this)
      )
    );
  }

  /**
   * Get entries with given vendor and salesOrg and (optional) endDate
   */
  public getEntries(cart: Cart, c: ConsignmentMetaData): OrderEntry[] {
    return cart.entries
      .filter((entry) => entry.product.vendor.code === c.vendor && this.getSalesOrg(entry) === c.salesOrg)
      .filter((entry) => (c.endDate ? c.endDate === this.getEndDate(entry) : true)); // compare endDate if it is provided
  }

  getSalesOrg(entry: OrderEntry): string {
    return this.getDataByType(entry.data, 'salesArea').salesOrganizationERP;
  }

  getEndDate(entry: OrderEntry): string {
    return this.getDataByType(entry.data, 'configuration').endDate;
  }

  getDataByType(data: Array<any>, type: string): any {
    return JSON.parse(data.find((el) => el.type === type).value);
  }

  getAddressByUsage(cart: Cart, usageType: string): Address {
    return cart.addresses.find((addr) => addr.addressUsage === usageType);
  }
}
